import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
    AsYouType,
    getCountries,
    getCountryCallingCode,
    parsePhoneNumberFromString,
} from 'libphonenumber-js';

const API_BASE_URL = (process.env.REACT_APP_API_URL || window.location.origin || '').replace(/\/$/, '');

const countryNames =
    typeof Intl !== 'undefined' && Intl.DisplayNames
        ? new Intl.DisplayNames(['en'], { type: 'region' })
        : null;

const COUNTRY_OPTIONS = getCountries().map((iso) => ({
    iso,
    name: countryNames?.of(iso) || iso,
    dialCode: `+${getCountryCallingCode(iso)}`,
})).sort((a, b) => {
    if (a.iso === 'US') return -1;
    if (b.iso === 'US') return 1;
    return a.name.localeCompare(b.name);
});

const getFlagEmoji = (iso) =>
    iso
        .toUpperCase()
        .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));

const formatPhoneInput = (value, countryIso) => new AsYouType(countryIso).input(value);
const getParsedPhoneNumber = (phoneNumber, country) => parsePhoneNumberFromString(phoneNumber.trim(), country.iso);

const getPhoneValidationMessage = (phoneNumber, country) => {
    if (!phoneNumber.trim()) return 'Enter a phone number.';

    const parsedPhoneNumber = getParsedPhoneNumber(phoneNumber, country);
    if (!parsedPhoneNumber || !parsedPhoneNumber.isValid()) {
        return `Enter a valid ${country.name} phone number.`;
    }

    if (parsedPhoneNumber.country && parsedPhoneNumber.country !== country.iso) {
        return `Select ${parsedPhoneNumber.country} or enter a ${country.name} phone number.`;
    }

    return '';
};

const ACCOUNT_DELETION_COPY = {
    happtrak: {
        app: 'happtrak',
        brandName: 'HappTrak.ai',
        homeLabel: 'Go to HappTrak home',
        logo: '/happtrak-logo.svg',
        logoAlt: 'HappTrak logo',
        heading: 'Request account deletion',
        intro: 'Enter the phone number associated with your HappTrak account and an email address where we can confirm the request.',
        success: 'Your deletion request was submitted. We will review it and follow up by email.',
    },
    jcal: {
        app: 'jcal',
        brandName: 'JCAL.app',
        homeLabel: 'Go to JCAL home',
        logo: '/jcal-logo-transparent.png',
        logoAlt: 'JCAL.app logo',
        heading: 'Request JCAL data deletion',
        intro: 'Enter the phone number associated with your JCAL account and an email address where we can confirm the request.',
        success: 'Your JCAL data deletion request was submitted. We will review it and follow up by email.',
    },
};

const AccountDeletionRequest = ({ app = 'happtrak' }) => {
    const copy = ACCOUNT_DELETION_COPY[app] || ACCOUNT_DELETION_COPY.happtrak;
    const [formData, setFormData] = useState({ email: '', phone_number: '', notes: '' });
    const [selectedCountry, setSelectedCountry] = useState(COUNTRY_OPTIONS[0]);
    const [countrySearch, setCountrySearch] = useState('');
    const [countryMenuOpen, setCountryMenuOpen] = useState(false);
    const [status, setStatus] = useState({ type: 'idle', message: '' });
    const [loading, setLoading] = useState(false);
    const countryPickerRef = useRef(null);

    const filteredCountries = COUNTRY_OPTIONS.filter((country) => {
        const query = countrySearch.trim().toLowerCase();
        if (!query) return true;
        return (
            country.name.toLowerCase().includes(query) ||
            country.iso.toLowerCase().includes(query) ||
            country.dialCode.includes(query)
        );
    });

    useEffect(() => {
        const handlePointerDown = (event) => {
            if (!countryPickerRef.current || countryPickerRef.current.contains(event.target)) return;
            setCountryMenuOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, []);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handlePhoneChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            phone_number: formatPhoneInput(value, selectedCountry.iso),
        }));
    };

    const selectCountry = (country) => {
        setSelectedCountry(country);
        setFormData((prev) => ({
            ...prev,
            phone_number: formatPhoneInput(prev.phone_number, country.iso),
        }));
        setCountrySearch('');
        setCountryMenuOpen(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!formData.email) return;

        const phoneValidationMessage = getPhoneValidationMessage(formData.phone_number, selectedCountry);
        if (phoneValidationMessage) {
            setStatus({ type: 'error', message: phoneValidationMessage });
            return;
        }

        setLoading(true);
        setStatus({ type: 'idle', message: '' });

        try {
            const parsedPhoneNumber = getParsedPhoneNumber(formData.phone_number, selectedCountry);
            await axios.post(`${API_BASE_URL || ''}/api/account-deletion-requests`, {
                app: copy.app,
                email: formData.email,
                phone_number: parsedPhoneNumber.number,
                notes: formData.notes,
            });

            setStatus({
                type: 'success',
                message: copy.success,
            });
            setFormData({ email: '', phone_number: '', notes: '' });
        } catch (error) {
            console.error('Unable to submit account deletion request', error);
            setStatus({
                type: 'error',
                message: 'We could not submit your request right now. Please try again in a moment.',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="home request-page">
            <div className="request-shell">
                <a href="/" className="request-brand" aria-label={copy.homeLabel}>
                    <img
                        src={`${process.env.PUBLIC_URL || ''}${copy.logo}`}
                        alt={copy.logoAlt}
                        className="request-logo"
                    />
                    <span>{copy.brandName}</span>
                </a>

                <div className="request-panel">
                    <div className="request-copy">
                        <h1>{copy.heading}</h1>
                        <p>{copy.intro}</p>
                    </div>

                    <form onSubmit={handleSubmit} className="beta-form">
                        <label className="field">
                            <span className="field-label">Email Address</span>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                autoComplete="email"
                                required
                            />
                        </label>

                        <div className="field">
                            <label className="field-label" htmlFor="deletion-phone">
                                Phone number
                            </label>
                            <div className="phone-input-row">
                                <div className="country-picker" ref={countryPickerRef}>
                                    <button
                                        type="button"
                                        className="country-button"
                                        onClick={() => setCountryMenuOpen((open) => !open)}
                                        aria-label="Select country code"
                                        aria-expanded={countryMenuOpen}
                                    >
                                        <span className="country-flag" aria-hidden="true">
                                            {getFlagEmoji(selectedCountry.iso)}
                                        </span>
                                        <span>{selectedCountry.dialCode}</span>
                                        <span className="country-caret" aria-hidden="true">⌄</span>
                                    </button>
                                    {countryMenuOpen && (
                                        <div className="country-menu">
                                            <input
                                                type="search"
                                                className="country-search"
                                                placeholder="Search country"
                                                value={countrySearch}
                                                onChange={(e) => setCountrySearch(e.target.value)}
                                                autoFocus
                                            />
                                            <div className="country-list" role="listbox">
                                                {filteredCountries.map((country) => (
                                                    <button
                                                        type="button"
                                                        key={country.iso}
                                                        className="country-option"
                                                        onClick={() => selectCountry(country)}
                                                        role="option"
                                                        aria-selected={country.iso === selectedCountry.iso}
                                                    >
                                                        <span className="country-flag" aria-hidden="true">
                                                            {getFlagEmoji(country.iso)}
                                                        </span>
                                                        <span className="country-option-name">{country.name}</span>
                                                        <span className="country-option-code">{country.dialCode}</span>
                                                    </button>
                                                ))}
                                                {filteredCountries.length === 0 && (
                                                    <div className="country-empty">No countries found</div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="tel"
                                    id="deletion-phone"
                                    name="phone"
                                    value={formData.phone_number}
                                    onChange={(e) => handlePhoneChange(e.target.value)}
                                    required
                                    inputMode="tel"
                                    autoComplete="tel"
                                    aria-label="Phone number"
                                />
                            </div>
                        </div>

                        <label className="field">
                            <span className="field-label">Additional details</span>
                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={(e) => handleChange('notes', e.target.value)}
                                maxLength="1000"
                            />
                        </label>

                        <button type="submit" className="cta-button" disabled={loading}>
                            {loading ? 'Submitting...' : 'Submit deletion request'}
                        </button>

                        {status.type !== 'idle' && (
                            <div className={`form-status ${status.type}`}>
                                {status.message}
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AccountDeletionRequest;
